import type {
  UpdateCallOffItemMutationOptions,
  UpdateCallOffItemVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCallOff } from '../queryKeys';

import { updateCallOffItem } from './updateCallOffItem';

/**
 * Custom hook for updating an existing line item in a call-off.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/update_calloff_item_calloffs__calloff_id__items__item_id__put
 */
export const useMutationUpdateCallOffItem = (
  options?: UpdateCallOffItemMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateCallOffItemVariables) =>
      updateCallOffItem(variables),
    onSuccess(_data, variables) {
      // Invalidate the call-off items list
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getItems(variables.callOffId),
      });
      // Invalidate the call-off detail (since items are included in the response)
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.get(variables.callOffId),
      });
    },
    ...options,
  });
};
